import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { IntakeFormData } from '../lib/types/domains/intake-form';
import { initialIntakeFormData } from '../lib/mockData/mockIntakeFormData';

interface FormState {
  formData: IntakeFormData;
  errors: Record<string, string>;
  isSubmitting: boolean;
  currentStep: number;
}

type FormAction = 
  | { type: 'UPDATE_FIELD'; path: string; value: any }
  | { type: 'SET_SECTION'; data: Partial<IntakeFormData> }
  | { type: 'SET_ERRORS'; errors: Record<string, string> }
  | { type: 'SET_SUBMITTING'; isSubmitting: boolean }
  | { type: 'SET_STEP'; step: number }
  | { type: 'RESET_FORM' };

const updateNestedField = (obj: any, path: string, value: any): any => {
  const keys = path.split('.');
  const result = JSON.parse(JSON.stringify(obj)); // Deep clone
  let current = result;
  
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!current[key]) {
      current[key] = {};
    }
    current = current[key];
  }
  
  current[keys[keys.length - 1]] = value;
  return result;
};

const formReducer = (state: FormState, action: FormAction): FormState => {
  switch (action.type) {
    case 'UPDATE_FIELD':
      return {
        ...state,
        formData: updateNestedField(state.formData, action.path, action.value),
        errors: { ...state.errors, [action.path]: '' } // Clear field error
      };
    
    case 'SET_SECTION':
      return {
        ...state,
        formData: { ...state.formData, ...action.data }
      };
    
    case 'SET_ERRORS':
      return {
        ...state,
        errors: action.errors
      };
    
    case 'SET_SUBMITTING':
      return {
        ...state,
        isSubmitting: action.isSubmitting
      };
    
    case 'SET_STEP':
      return {
        ...state,
        currentStep: action.step
      };
    
    case 'RESET_FORM':
      return {
        formData: initialIntakeFormData,
        errors: {},
        isSubmitting: false,
        currentStep: 0
      };
    
    default:
      return state;
  }
};

interface IntakeFormContextType {
  formData: IntakeFormData;
  errors: Record<string, string>;
  isSubmitting: boolean;
  currentStep: number;
  updateField: (path: string, value: any) => void;
  setSection: (data: Partial<IntakeFormData>) => void;
  setErrors: (errors: Record<string, string>) => void;
  setSubmitting: (isSubmitting: boolean) => void;
  setStep: (step: number) => void;
  resetForm: () => void;
}

const IntakeFormContext = createContext<IntakeFormContextType | undefined>(undefined);

export const useIntakeForm = () => {
  const context = useContext(IntakeFormContext);
  if (!context) {
    throw new Error('useIntakeForm must be used within an IntakeFormProvider');
  }
  return context;
};

interface IntakeFormProviderProps {
  children: ReactNode;
  initialData?: IntakeFormData;
}

export const IntakeFormProvider: React.FC<IntakeFormProviderProps> = ({ 
  children, 
  initialData 
}) => {
  // Ensure all nested objects exist in the initial state
  const ensureCompleteFormData = (data: any): IntakeFormData => {
    return {
      personalInfo: data?.personalInfo || {},
      practitionerQuestions: data?.practitionerQuestions || {},
      healthConcerns: data?.healthConcerns || {},
      preExistingConditions: data?.preExistingConditions || {},
      familyMedicalHistory: data?.familyMedicalHistory || {},
      lifestyleProfile: data?.lifestyleProfile || {},
      sleepProfile: data?.sleepProfile || {},
      dentalProfile: data?.dentalProfile || {},
      toxinScreen: data?.toxinScreen || {},
      physicalActivity: data?.physicalActivity || {},
      psychosocialProfile: data?.psychosocialProfile || {},
      dietaryProfile: data?.dietaryProfile || {},
      supplementsMedications: data?.supplementsMedications || {},
      symptomAssessment: data?.symptomAssessment || {},
      healthAndMedicalConditions: data?.healthAndMedicalConditions || {},
      clientStatement: data?.clientStatement || {}
    };
  };

  const [state, dispatch] = useReducer(formReducer, {
    formData: ensureCompleteFormData(initialData || initialIntakeFormData),
    errors: {},
    isSubmitting: false,
    currentStep: 0
  });

  const updateField = (path: string, value: any) => {
    console.log('updateField called:', path, value);
    dispatch({ type: 'UPDATE_FIELD', path, value });
  };

  const setSection = (data: Partial<IntakeFormData>) => {
    dispatch({ type: 'SET_SECTION', data });
  };

  const setErrors = (errors: Record<string, string>) => {
    dispatch({ type: 'SET_ERRORS', errors });
  };

  const setSubmitting = (isSubmitting: boolean) => {
    dispatch({ type: 'SET_SUBMITTING', isSubmitting });
  };

  const setStep = (step: number) => {
    dispatch({ type: 'SET_STEP', step });
  };

  const resetForm = () => {
    dispatch({ type: 'RESET_FORM' });
  };

  const value: IntakeFormContextType = {
    formData: state.formData,
    errors: state.errors,
    isSubmitting: state.isSubmitting,
    currentStep: state.currentStep,
    updateField,
    setSection,
    setErrors,
    setSubmitting,
    setStep,
    resetForm
  };

  return (
    <IntakeFormContext.Provider value={value}>
      {children}
    </IntakeFormContext.Provider>
  );
};